/**
 * Manuel Giriş/Çıkış Kayıt Modal'ı
 * BOSS panelinden geçmiş tarih/saat için kayıt ekleme.
 */

import { useEffect, useState } from 'react'
import {
  ArrowDownToLine, ArrowUpFromLine, Save, AlertCircle, Clock,
} from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { DatePicker } from '@/components/ui/DatePicker'
import { manuelKayitOlustur } from '@/lib/services/kayit-service'
import { tumPersonelleriGetir } from '@/lib/services/personel-service'
import { tumSubeleriGetir } from '@/lib/services/sube-service'
import type { KullaniciPersonel, Sube } from '@/types'

interface Props {
  acik: boolean
  onKapat: () => void
  onKaydedildi?: () => void
  /** Verilirse personel sabittir (kart üzerinden açıldığında), dropdown kilitli. */
  sabitPersonelId?: string
}

function bugunIso(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function suAnSaat(): string {
  const d = new Date()
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

export function ManuelKayitEkleModal({ acik, onKapat, onKaydedildi, sabitPersonelId }: Props) {
  const [personeller, setPersoneller] = useState<KullaniciPersonel[]>([])
  const [subeler, setSubeler] = useState<Sube[]>([])
  const [yukleniyor, setYukleniyor] = useState(false)

  const [personelId, setPersonelId] = useState('')
  const [subeId, setSubeId] = useState('')
  const [tip, setTip] = useState<'giris' | 'cikis'>('giris')
  const [tarih, setTarih] = useState(bugunIso())
  const [saat, setSaat] = useState(suAnSaat())
  const [notlar, setNotlar] = useState('')

  const [gonderiliyor, setGonderiliyor] = useState(false)
  const [hata, setHata] = useState<string | null>(null)

  useEffect(() => {
    if (!acik) return
    setYukleniyor(true)
    Promise.all([tumPersonelleriGetir(), tumSubeleriGetir()])
      .then(([p, s]) => {
        setPersoneller(p.filter((x) => x.aktif))
        setSubeler(s.filter((x) => x.aktif))
        setPersonelId(sabitPersonelId ?? '')
        setSubeId('')
        setTip('giris')
        setTarih(bugunIso())
        setSaat(suAnSaat())
        setNotlar('')
        setHata(null)
      })
      .catch((err) => {
        console.error(err)
        setHata('Veri yüklenemedi')
      })
      .finally(() => setYukleniyor(false))
  }, [acik, sabitPersonelId])

  // Personele atanan şubeler
  const secilenPersonel = personeller.find((p) => p.id === personelId)
  const personelinSubeleri = secilenPersonel
    ? subeler.filter((s) => secilenPersonel.subeIdleri?.includes(s.id))
    : subeler

  // Personel değişince şube auto-set (tek şubesi varsa)
  useEffect(() => {
    if (personelinSubeleri.length === 1) {
      setSubeId(personelinSubeleri[0]!.id)
    } else if (!personelinSubeleri.some((s) => s.id === subeId)) {
      setSubeId('')
    }
  }, [personelId]) // eslint-disable-line

  async function kaydet() {
    if (!personelId) { setHata('Personel seç'); return }
    if (!subeId) { setHata('Şube seç'); return }
    if (!tarih) { setHata('Tarih seç'); return }
    if (!saat) { setHata('Saat gir'); return }

    setGonderiliyor(true)
    setHata(null)
    try {
      const [y, m, d] = tarih.split('-').map(Number)
      const [h, dk] = saat.split(':').map(Number)
      const zaman = new Date(y!, m! - 1, d!, h!, dk!, 0, 0)

      if (zaman.getTime() > Date.now()) {
        setHata('Gelecek tarihli kayıt eklenemez')
        setGonderiliyor(false)
        return
      }

      await manuelKayitOlustur({
        personelId,
        subeId,
        tip,
        zaman,
        yontem: 'manuelBoss',
        notlar: notlar.trim() || undefined,
      })
      onKaydedildi?.()
      onKapat()
    } catch (err) {
      console.error(err)
      setHata((err as Error).message)
    } finally {
      setGonderiliyor(false)
    }
  }

  return (
    <Modal acik={acik} onKapat={onKapat} baslik="Manuel Kayıt Ekle" boyut="md">
      {yukleniyor ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600"></div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-900">
              Bu kayıt <strong>BOSS manuel kayıt</strong> olarak işaretlenir. Geçmiş tarihli kayıt eklenebilir; gelecek tarihli eklenemez.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Personel <span className="text-red-500">*</span>
            </label>
            <select
              value={personelId}
              onChange={(e) => setPersonelId(e.target.value)}
              disabled={!!sabitPersonelId}
              className="input-base disabled:opacity-60 disabled:bg-gray-50"
            >
              <option value="">— Seç —</option>
              {personeller.map((p) => (
                <option key={p.id} value={p.id}>{p.isim}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Şube <span className="text-red-500">*</span>
            </label>
            <select
              value={subeId}
              onChange={(e) => setSubeId(e.target.value)}
              disabled={!personelId}
              className="input-base disabled:opacity-50"
            >
              <option value="">— Seç —</option>
              {personelinSubeleri.map((s) => (
                <option key={s.id} value={s.id}>{s.isim}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Tip <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setTip('giris')}
                className={`px-3 py-2.5 rounded-xl border-2 font-semibold inline-flex items-center justify-center gap-2 transition-colors ${
                  tip === 'giris'
                    ? 'bg-green-50 border-green-500 text-green-700'
                    : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                <ArrowDownToLine className="w-4 h-4" />
                Giriş
              </button>
              <button
                type="button"
                onClick={() => setTip('cikis')}
                className={`px-3 py-2.5 rounded-xl border-2 font-semibold inline-flex items-center justify-center gap-2 transition-colors ${
                  tip === 'cikis'
                    ? 'bg-orange-50 border-orange-500 text-orange-700'
                    : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                <ArrowUpFromLine className="w-4 h-4" />
                Çıkış
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Tarih <span className="text-red-500">*</span>
              </label>
              <DatePicker
                value={tarih}
                onChange={setTarih}
                max={bugunIso()}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Saat <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="time"
                  value={saat}
                  onChange={(e) => setSaat(e.target.value)}
                  className="input-base"
                />
                <Clock className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Not (opsiyonel)
            </label>
            <textarea
              value={notlar}
              onChange={(e) => setNotlar(e.target.value)}
              rows={2}
              maxLength={200}
              placeholder="Örn: Telefon bozuk, BOSS manuel ekledi"
              className="input-base resize-none"
            />
          </div>

          {hata && (
            <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-xl p-3">
              {hata}
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onKapat}
              disabled={gonderiliyor}
              className="btn-secondary flex-1"
            >
              İptal
            </button>
            <button
              type="button"
              onClick={kaydet}
              disabled={gonderiliyor}
              className="btn-primary flex-1 inline-flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" />
              {gonderiliyor ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  )
}
